import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { landingForUser } from "@/lib/roleRedirect";

/**
 * Jun 2026 — Social login completion. The backend callback redirects here
 * with the AWARE JWT in the URL FRAGMENT (never sent to any server or
 * logged). We store it, load the user via the existing /auth/me, then
 * route with the existing landingForUser() role router or the safe ?next=.
 */
export default function AuthComplete() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const frag = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = frag.get("token");
    const next = frag.get("next");
    const isNew = frag.get("new") === "1";
    // Strip the fragment immediately so the token never lingers in history.
    window.history.replaceState(null, "", "/auth/complete");
    if (!token) {
      navigate("/login?social_error=invalid_state", { replace: true });
      return;
    }
    localStorage.setItem("scwt_token", token);
    (async () => {
      try {
        const { data: user } = await api.get("/auth/me");
        await refresh();
        toast.success(isNew
          ? `Welcome to AWARE, ${user.full_name.split(" ")[0]}!`
          : `Welcome back, ${user.full_name.split(" ")[0]}`);
        const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
        navigate(isNew && user.role === "customer" ? "/welcome" : (safeNext || landingForUser(user)), { replace: true });
      } catch {
        localStorage.removeItem("scwt_token");
        navigate("/login?social_error=provider_error", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div data-testid="auth-complete-page" className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto" />
        <p className="mt-3 text-sm font-semibold text-slate-600">Signing you in…</p>
      </div>
    </div>
  );
}
